/**
 * Shrinks an image blob before it is ever handed to an `<img>`.
 *
 * The payments table draws payment proofs at 40px. A proof is whatever the customer uploaded —
 * `UploadFileCommandValidator` accepts up to 5 MB, and in practice it is a phone photo of a bank
 * slip — and the download endpoint streams the original bytes, so a 40px tile was rendering a
 * twelve-megapixel image. Decoded, that is ~48 MB of bitmap per tile; a page of fifty payments
 * held enough of them at once to make scrolling stutter and the tab swap.
 *
 * `<img>` cannot avoid that: `object-cover` scales what is drawn, not what is decoded. So the
 * bytes are resampled once, here, and what reaches the DOM is a thumbnail.
 *
 * `createImageBitmap` decodes off the main thread in every browser that has it, which is the
 * other half of the win — the expensive part no longer competes with scrolling.
 */

/**
 * Quality of the re-encoded thumbnail. Well below the default: the output is shown at 40–48px,
 * where the artefacts are invisible and the size difference is not.
 */
const THUMBNAIL_QUALITY = 0.8;

/**
 * Draws a bitmap into a canvas at the given size and returns it as a blob.
 *
 * `OffscreenCanvas` keeps the work off the document, but Safari only grew `convertToBlob` in
 * 16.4, so a detached `<canvas>` is the fallback rather than giving up on the resize.
 */
async function encodeAtSize(
  bitmap: ImageBitmap,
  width: number,
  height: number,
): Promise<Blob | null> {
  if (typeof OffscreenCanvas !== "undefined") {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext("2d");
    if (!context) {
      return null;
    }

    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.convertToBlob({ type: "image/webp", quality: THUMBNAIL_QUALITY });
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }

  context.drawImage(bitmap, 0, 0, width, height);

  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/webp", THUMBNAIL_QUALITY);
  });
}

/**
 * Returns `blob` resampled so its width is at most `maxWidth`, or `blob` itself when it cannot
 * be — or need not be — resized.
 *
 * Never throws and never upscales. Every failure path returns the original blob: a thumbnail
 * that is merely bigger than it had to be still shows the operator their receipt, whereas a
 * rejected promise would turn a loadable proof into the "N/A" tile. That covers the file that
 * is not really an image, a canvas the browser refuses, and jsdom, which has neither
 * `createImageBitmap` nor a canvas and so leaves tests on the original bytes.
 */
export async function downscaleImageBlob(blob: Blob, maxWidth: number): Promise<Blob> {
  if (typeof createImageBitmap === "undefined" || maxWidth <= 0) {
    return blob;
  }

  let bitmap: ImageBitmap | null = null;

  try {
    bitmap = await createImageBitmap(blob);

    // Already small enough. Re-encoding it would cost a decode and could only make it worse.
    if (bitmap.width <= maxWidth) {
      return blob;
    }

    const height = Math.max(1, Math.round((bitmap.height * maxWidth) / bitmap.width));
    const resized = await encodeAtSize(bitmap, maxWidth, height);

    // A resize that came back bigger than the original is not a saving — some small PNGs
    // re-encode larger — so keep whichever is smaller.
    return resized && resized.size < blob.size ? resized : blob;
  } catch {
    return blob;
  } finally {
    bitmap?.close();
  }
}
